import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { fetchPaymentIntent } from 'store/actions/order'
import * as appActions from 'store/actions/app'

const PaymentForm = ({ onSuccess }) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements || submitting) { return }

    setSubmitting(true)
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: 'if_required',
    })
    setSubmitting(false)

    if (error) {
      dispatch(appActions.appErrors([error.message]))
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <div
        className={submitting || !stripe ? 'button-disabled' : 'button'}
        onClick={!submitting && stripe ? handleSubmit : undefined}
        style={{ marginTop: '16px' }}
      >
        {submitting ? 'Processing...' : 'Submit Payment'}
      </div>
    </form>
  )
}

const BorrowerPaymentForm = ({ activePayment, changeStep }) => {
  const dispatch = useDispatch()
  const token = useSelector(state => state.app.token)
  const orderId = useSelector(state => state.order.id)
  const [clientSecret, setClientSecret] = useState(null)
  const [stripe, setStripe] = useState(null)
  const [initError, setInitError] = useState(false)

  // The payment form needs two independent things before it can mount: Stripe.js (fetched
  // from js.stripe.com at runtime) and a PaymentIntent client secret (from our backend).
  // On a flaky connection or with a content blocker either — or both — can fail, which
  // previously left the borrower on a blank step (and the Stripe.js failure as an unhandled
  // rejection). Treat both as one recoverable state: "Try again" re-attempts only whichever
  // half is still missing. loadStripe() resets its internal promise and re-injects the
  // script tag on failure, so retrying it is a genuine reload; the PI fetch is guarded on
  // !clientSecret so a retry never creates a duplicate PaymentIntent.
  const initialize = useCallback(() => {
    setInitError(false)

    if (!stripe) {
      loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY)
        .then((stripeInstance) => {
          if (!stripeInstance) { throw new Error('Stripe.js unavailable') }
          setStripe(stripeInstance)
        })
        .catch(() => setInitError(true))
    }

    if (!clientSecret) {
      dispatch(fetchPaymentIntent(token, orderId))
        .then(({ client_secret }) => setClientSecret(client_secret))
        .catch(() => setInitError(true))
    }
  }, [stripe, clientSecret, dispatch, token, orderId])

  // Mount-once: initialize re-runs only the missing half, so we intentionally do not
  // re-fire the effect when its dependencies (stripe/clientSecret) settle.
  useEffect(() => {
    initialize()
  }, [])

  if (initError) {
    return (
      <div className="flex-container text-center">
        <div className="box fullwidth">
          <p style={{ marginBottom: '16px' }}>
            We were unable to load the payment form. Please check your connection and try again.
          </p>
          <div className="button" onClick={initialize} style={{ marginTop: '16px' }}>
            Try again
          </div>
        </div>
      </div>
    )
  }

  if (!stripe || !clientSecret) { return null }

  return (
    <div className="flex-container text-center">
      <div className="box fullwidth">
        <p style={{ marginBottom: '16px' }}>
          Your payment is <b>${(activePayment.up_to_amount
          ? activePayment.up_to_amount
          : activePayment.amount / 100).toFixed(2)}</b>
          <br />
          All payments must be made before proceeding with the appraisal
        </p>
        <Elements stripe={stripe} options={{ clientSecret }}>
          <PaymentForm onSuccess={changeStep} />
        </Elements>
      </div>
    </div>
  )
}

export default BorrowerPaymentForm
